<script setup lang="ts">
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

export interface Props {
  label?: string,
  disabled?: boolean,
  btnStyle?: "primary" | "secondary" | "tertiary" | "info" | "warning",
  size?: "xl" | "lg" | "md" | "sm" | "xs",
  type?: "button" | "submit" | "reset",
  leadingIcon?: IconDefinition,
  trailingIcon?: IconDefinition,
  expand?: boolean,
  destructive?: boolean,
  customSidePadding?: number,
  green?: boolean,
  loading?: boolean,
}
const props = withDefaults(defineProps<Props>(), {
  label: "",
  btnStyle: "primary",
  size: "lg",
  type: "button",
  leadingIcon: undefined,
  trailingIcon: undefined,
  customSidePadding: undefined,
  green: false,
  loading: false,
});
</script>

<template>
  <button
    :disabled="props.disabled || props.loading"
    :type="props.type"
    :class="['btn', props.btnStyle, props.size, {
      expand: props.expand,
      destructive: props.destructive,
      green: props.green,
      loading: props.loading
    }]"
    :style="{
      paddingRight: `${props.customSidePadding}px`,
      paddingLeft: `${props.customSidePadding}px`,
    }"
  >
    <div v-if="props.loading" class="spinner-container">
      <div class="button-spinner" />
    </div>
    <div class="button-content" :class="{ 'invisible': props.loading }">
      <FontAwesomeIcon v-if="props.leadingIcon" :icon="props.leadingIcon" />
      {{ props.label }}
      <FontAwesomeIcon v-if="props.trailingIcon" :icon="props.trailingIcon" />
    </div>
  </button>
</template>

<style lang="scss">
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.btn {
  border: none;
  border-radius: map.get(v.$border-radius, "max");
  font-family: inherit;
  font-weight: map.get(v.$font-weight, "medium");
  text-wrap: nowrap;
  line-height: 1.5;
  cursor: pointer;
  transition: background-color .2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: .75em;
  position: relative;
  .button-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: .75em;
    &.invisible {
      visibility: hidden;
    }
  }
  .spinner-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    .button-spinner {
      border-radius: 50%;
      border-style: solid;
      border-color: map.get(v.$gray, "200");
      border-left-color: map.get(v.$primary, "500");
      animation: spin 1s ease infinite;
      width: 1em;
      height: 1em;
      border-width: 2px;
    }
  }
  &.xs .spinner-container .button-spinner {
    width: 0.75rem;
    height: 0.75rem;
    border-width: 1.5px;
  }
  &.sm .spinner-container .button-spinner {
    width: 0.875rem;
    height: 0.875rem;
    border-width: 1.5px;
  }
  &.md .spinner-container .button-spinner {
    width: 0.875rem;
    height: 0.875rem;
    border-width: 1.5px;
  }
  &.lg .spinner-container .button-spinner {
    width: 1rem;
    height: 1rem;
    border-width: 2px;
  }
  &.xl .spinner-container .button-spinner {
    width: 1.125rem;
    height: 1.125rem;
    border-width: 2px;
  }
  &.expand {
    flex: 1 1 0px;
    width: 100%;
  }
  &.xs {
    padding: 5px 12px;
    font-size: .75rem;
  }
  &.sm {
    padding: 6px 14px;
    font-size: .875rem;
  }
  &.md {
    padding: 8px 16px;
    font-size: .875rem;
  }
  &.lg {
    padding: 10px 20px;
    font-size: 1rem;
  }
  &.xl {
    padding: 13px 25px;
    font-size: 1.125rem;
  }
  &.primary {
    background-color: map.get(v.$primary, "600");
    color: white;
    &:hover {
      background-color: map.get(v.$primary, "500");
    }
    &.green {
      background-color: map.get(v.$secondary, "700");
      color: white;
      border: 1px solid map.get(v.$secondary, "50");
      &:hover {
        background-color: map.get(v.$secondary, "600");
      }
    }
    &.destructive {
      background-color: map.get(v.$error, "700");
      &:hover {
        background-color: map.get(v.$error, "500");
      }
    }
  }
  &.secondary {
    background-color: white;
    color: map.get(v.$gray, "700");
    border: 1px solid map.get(v.$gray, "300");
    &:hover {
      background-color: map.get(v.$gray, "100");
    }
    &.green {
      background-color: map.get(v.$secondary, "50");
      color: map.get(v.$secondary, "700");
      border: 1px solid map.get(v.$secondary, "700");
      &:hover {
        background-color: map.get(v.$secondary, "100");
      }
    }
    &.destructive {
      background-color: map.get(v.$error, "25");
      color: map.get(v.$error, "700");
      border: 1px solid map.get(v.$error, "200");
      &:hover {
        background-color: map.get(v.$error, "50");
        color: map.get(v.$error, "800");
      }
    }
  }
  &.tertiary {
    color: map.get(v.$primary, "600");
    background-color: map.get(v.$primary, "50");
    &:hover {
      background-color: map.get(v.$primary, "100");
    }
    &.destructive {
      background-color: map.get(v.$error, "100");
      color: map.get(v.$error, "700");
      &:hover {
        background-color: map.get(v.$error, "200");
        color: map.get(v.$error, "800");
      }
    }
  }
  &.info {
    background-color: map.get(v.$info, "500");
    color: white;
    &:hover {
      background-color: map.get(v.$info, "400");
    }
  }
  &.warning {
    background-color: map.get(v.$warning, "600");
    color: white;
    &:hover {
      background-color: map.get(v.$warning, "500");
    }
  }
  &:disabled {
    opacity: .5;
    cursor: unset;
    pointer-events: none;
  }
}
</style>
